import moment from 'moment';
import type { VoidFunctionComponent } from 'react';

import { useQuery } from '../../../contexts/RouterContext';
import FallbackImage from '../../generic/FallbackImage';
import Loader from '../../generic/Loader';
import BuildList from '../BuildList';
import type { RouterProps } from '../../../types';

import BuildListHeader from './BuildListHeader';
import useBuildList from './useBuildList';
import type { Query } from './useBuildList';

type ProjectBuildsPageProps = RouterProps<{ project_id: string }>;

// convert query params into useBuildList.Query
const parserFn = (store: Record<string, any>): Query => {
  return {
    page: store.page ? Number(store.page) : 0,
    pageSize: store.pageSize ? Number(store.pageSize) : 20,
    startDate: store.startDate || moment().startOf('day').subtract(6, 'months').toISOString(),
    endDate: store.endDate || moment().endOf('day').toISOString(),
  };
};

const displayDateRange = (startDate: string, endDate: string) =>
  `${moment(startDate).format('MMMM Do, YYYY')} - ${moment(endDate).format('MMMM Do, YYYY')}`;

const ProjectBuildsPage: VoidFunctionComponent<ProjectBuildsPageProps> = (
  props,
) => {
  const [query, setQuery] = useQuery<Query>(parserFn);
  // Grab Build Data
  const {
    builds,
    buildStatus: { isLoading: isBuildLoading, errors: buildErrors },
    count,
    countStatus: { isLoading: isCountLoading, errors: countErrors },
  } = useBuildList({
    query,
    mode: 'PROJECT',
    locator: props.params.project_id,
  });

  const onPageNext = () =>
    setQuery({
      ...query,
      page: query.page + 1,
    });

  const onPagePrevious = () =>
    setQuery({
      ...query,
      page: query.page - 1,
    });

  const onSetDateRange = (dateRange: { startDate: Date; endDate: Date }) =>
    setQuery({
      ...query,
      startDate: moment(dateRange.startDate).startOf('day').toISOString(),
      endDate: moment(dateRange.endDate).endOf('day').toISOString(),
      page: 0,
    });

  let buildList;
  if (buildErrors?.length || countErrors?.length) {
    buildList = (
      <div className="panel">
        <FallbackImage
          src="error-plug"
          alt="Error fetching issue"
          hasPadding
        >
          Error fetching Builds
        </FallbackImage>
      </div>
    );
  } else if (isBuildLoading) {
    buildList = <Loader />;
  } else {
    buildList = (
      <BuildList
        builds={builds}
      >
        <div className="panel">
          <FallbackImage
            src="no-results"
            alt="No results found"
            hasPadding
          >
            {`No builds found for ${displayDateRange(query.startDate, query.endDate)}`}
          </FallbackImage>
        </div>
      </BuildList>
    );
  }

  return (
    <div className="container">
      <BuildListHeader
        startDate={query.startDate}
        endDate={query.endDate}
        page={query.page}
        pageSize={query.pageSize}
        onSetDateRange={onSetDateRange}
        totalCount={count}
        onPageNext={onPageNext}
        onPagePrevious={onPagePrevious}
        isLoading={isCountLoading}
      />
      {buildList}
    </div>
  );
};

export default ProjectBuildsPage;
